// webapp/src/lib/useAbortableSupabaseQuery.ts
//
// Phase 31.6 Plan 03 (Wave 1 GREEN) — FE-31-01 / FE-31-21 / D-06.
//
// Belt-and-braces unmount guard for a single Supabase read-in-effect. Generalizes
// the existing `cancelled`-flag effect at SlotPicker.tsx (phone prefill twin) and
// mirrors the server-side AbortController discipline in
// `supabase/functions/_shared/fetchWithTimeout.ts` (D-06).
//
// Guarantee: if the in-flight query resolves AFTER the consuming component
// unmounts (or `deps` change), `onData` is NOT called and the underlying
// postgrest request is aborted. Both protections are kept on purpose:
//   - `cancelled` flag → makes the "no setState after unmount" guarantee
//     deterministic even if the transport ignores the signal.
//   - `ac.abort()`     → cancels the in-flight fetch so we stop wasting the
//     connection and the abort propagates to `.abortSignal(signal)`.
//
// The caller attaches the signal via postgrest's chainable
// `.abortSignal(signal)` (confirmed on installed postgrest-js 2.103.3):
//   useAbortableSupabaseQuery(
//     (signal) => supabase.from('credits_balance').select('balance, expires_at')
//       .eq('user_id', user.id).abortSignal(signal).maybeSingle(),
//     ({ ... }) => setHasPack(...),
//     [user?.id],
//   )
//
// Refs: phase 31.6

import { useEffect } from 'react'

export function useAbortableSupabaseQuery<T>(
  queryFactory: (signal: AbortSignal) => PromiseLike<{ data: T | null; error: unknown }>,
  onData: (data: T | null) => void,
  deps: React.DependencyList,
): void {
  useEffect(() => {
    const ac = new AbortController()
    let cancelled = false
    queryFactory(ac.signal).then(({ data }) => {
      if (!cancelled) onData(data)
    })
    return () => {
      cancelled = true
      ac.abort()
    }
    // deps are caller-supplied (same contract as the existing SlotPicker effects).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}
